import type { MessageKey, TranslationParams } from '../i18n';
import type { ResultFilter, ResultSort, UiRecord } from './types';
import { formatBytes, formatPercent, normalizeAnalysis } from './utils';

export interface ResultTableState {
  records: UiRecord[];
  visibleRecords: UiRecord[];
  selectedIds: Set<string>;
  activeId?: string;
  search: string;
  filter: ResultFilter;
  sort: ResultSort;
}

export interface ResultTableOptions {
  translate: (key: MessageKey, params?: TranslationParams) => string;
  onSearch: (value: string) => void;
  onFilter: (value: ResultFilter) => void;
  onSort: (value: ResultSort) => void;
  onToggleAll: (checked: boolean) => void;
  onToggleRecord: (id: string, checked: boolean) => void;
  onOpen: (id: string) => void;
  onDelete: (id: string) => void;
  onReanalyze: (id: string) => void;
  onDeleteSelected: () => void;
  onReanalyzeSelected: () => void;
  onClear: () => void;
  onExportJson: () => void;
  onExportCsv: () => void;
}

export function renderResultTable(state: ResultTableState, options: ResultTableOptions): string {
  const t = options.translate;
  const readyCount = state.records.filter((record) => record.status === 'ready').length;
  const selectedVisible = state.visibleRecords.filter((record) =>
    state.selectedIds.has(record.id),
  ).length;
  const allVisibleSelected =
    state.visibleRecords.length > 0 && selectedVisible === state.visibleRecords.length;
  return `
    <section class="results-panel" aria-labelledby="results-title" data-testid="results-section">
      <div class="section-heading results-heading">
        <div>
          <h2 id="results-title">${t('results')}</h2>
          <p aria-live="polite" data-testid="result-count">${t('resultCount', { count: state.visibleRecords.length })}</p>
        </div>
        <div class="export-actions" aria-label="${t('reportExport')}">
          <button class="button button--secondary" type="button" data-action="export-json" data-testid="export-json" ${readyCount === 0 ? 'disabled' : ''}>
            <span aria-hidden="true">↓</span>${t('exportJson')}
          </button>
          <button class="button button--secondary" type="button" data-action="export-csv" data-testid="export-csv" ${readyCount === 0 ? 'disabled' : ''}>
            <span aria-hidden="true">↓</span>${t('exportCsv')}
          </button>
        </div>
      </div>

      <div class="table-toolbar">
        <div class="field field--search">
          <label for="result-search">${t('searchLabel')}</label>
          <div class="input-with-icon">
            <span aria-hidden="true">⌕</span>
            <input id="result-search" type="search" value="${escapeAttribute(state.search)}" placeholder="${t('searchPlaceholder')}" data-testid="search-input" />
          </div>
        </div>
        <div class="field">
          <label for="result-filter">${t('filterLabel')}</label>
          <select id="result-filter" data-testid="status-filter">
            ${option('all', t('filterAll'), state.filter)}
            ${option('ready', t('filterReady'), state.filter)}
            ${option('transparent', t('filterTransparent'), state.filter)}
            ${option('opaque', t('filterOpaque'), state.filter)}
            ${option('suspected', t('filterSuspected'), state.filter)}
            ${option('progress', t('filterProgress'), state.filter)}
            ${option('error', t('filterError'), state.filter)}
          </select>
        </div>
        <div class="field">
          <label for="result-sort">${t('sortLabel')}</label>
          <select id="result-sort" data-testid="sort-select">
            ${option('added', t('sortAdded'), state.sort)}
            ${option('name', t('sortName'), state.sort)}
            ${option('transparent-desc', t('sortTransparentDesc'), state.sort)}
            ${option('transparent-asc', t('sortTransparentAsc'), state.sort)}
          </select>
        </div>
      </div>

      <div class="selection-toolbar" aria-label="${t('batchActions')}">
        <label class="checkbox-control">
          <input type="checkbox" data-action="toggle-all" data-testid="select-all" ${allVisibleSelected ? 'checked' : ''} ${state.visibleRecords.length === 0 ? 'disabled' : ''} />
          <span>${t('selectAll')}</span>
        </label>
        <span class="selection-count" data-testid="selected-count">${t('selectedCount', { count: state.selectedIds.size })}</span>
        <div class="selection-toolbar__actions">
          <button class="button button--quiet" type="button" data-action="reanalyze-selected" data-testid="reanalyze-selected" ${state.selectedIds.size === 0 ? 'disabled' : ''}>
            <span aria-hidden="true">↻</span>${t('reanalyzeSelected')}
          </button>
          <button class="button button--quiet button--danger" type="button" data-action="delete-selected" data-testid="delete-selected" ${state.selectedIds.size === 0 ? 'disabled' : ''}>
            <span aria-hidden="true">×</span>${t('deleteSelected')}
          </button>
          <button class="button button--quiet" type="button" data-action="clear" data-testid="clear-all" ${state.records.length === 0 ? 'disabled' : ''}>
            ${t('clearAll')}
          </button>
        </div>
      </div>

      <div class="table-scroll" data-testid="results-table">
        <table>
          <thead>
            <tr>
              <th class="column-select"><span class="visually-hidden">${t('selectAll')}</span></th>
              <th class="column-preview">${t('tablePreview')}</th>
              <th>${t('tableFile')}</th>
              <th>${t('tableSize')}</th>
              <th>${t('tableAlpha')}</th>
              <th>${t('tableTransparent')}</th>
              <th>${t('tableSemi')}</th>
              <th>${t('tablePadding')}</th>
              <th>${t('tableRisk')}</th>
              <th>${t('tableStatus')}</th>
              <th class="column-actions">${t('tableActions')}</th>
            </tr>
          </thead>
          <tbody>
            ${renderRows(state, options)}
          </tbody>
        </table>
      </div>
    </section>
  `;
}

function renderRows(state: ResultTableState, options: ResultTableOptions): string {
  const t = options.translate;
  if (state.visibleRecords.length === 0) {
    const filtered = state.records.length > 0;
    return `
      <tr class="empty-row">
        <td colspan="11">
          <div class="empty-state" data-testid="${filtered ? 'filtered-empty-state' : 'results-empty-state'}">
            <span class="empty-state__icon" aria-hidden="true">▧</span>
            <strong>${t(filtered ? 'emptyFilteredTitle' : 'emptyTitle')}</strong>
            <p>${t(filtered ? 'emptyFilteredBody' : 'emptyBody')}</p>
          </div>
        </td>
      </tr>
    `;
  }

  return state.visibleRecords
    .map((record) => {
      const checked = state.selectedIds.has(record.id);
      const active = state.activeId === record.id;
      const analysis = normalizeAnalysis(record);
      const dimensions =
        analysis.width > 0 && analysis.height > 0
          ? `${analysis.width} × ${analysis.height}`
          : t('unknownDimensions');
      const padding = analysis.boundingBox
        ? t('paddingValues', { ...analysis.margins })
        : t('notAvailable');
      const risk = analysis.suspected
        ? `<span class="status status--warning"><span aria-hidden="true">!</span>${t('statusSuspected')}</span>`
        : record.status === 'ready'
          ? `<span class="muted-value">${t('none')}</span>`
          : `<span class="muted-value">${t('notAvailable')}</span>`;
      return `
        <tr class="result-row${active ? ' is-active' : ''}" data-record-id="${record.id}" data-testid="result-row" aria-selected="${String(active)}">
          <td class="column-select">
            <input type="checkbox" data-action="toggle-record" data-record-id="${record.id}" aria-label="${t('selectFile', { name: escapeAttribute(record.file.name) })}" ${checked ? 'checked' : ''} data-testid="row-select" />
          </td>
          <td class="column-preview">
            <button class="thumbnail-button" type="button" data-action="open" data-record-id="${record.id}" aria-label="${t('openDetails', { name: escapeAttribute(record.file.name) })}" data-testid="row-preview">
              <span class="thumbnail-checker"><img src="${escapeAttribute(record.objectUrl)}" alt="" /></span>
            </button>
          </td>
          <td>
            <button class="file-link" type="button" data-action="open" data-record-id="${record.id}" title="${escapeAttribute(record.file.name)}" data-testid="row-file-name">${escapeHtml(record.file.name)}</button>
          </td>
          <td><span class="mono-value">${dimensions}</span><small>${formatBytes(record.file.size)}</small></td>
          <td>${renderAlphaStatus(record, analysis.realTransparency, t)}</td>
          <td class="mono-value">${record.status === 'ready' ? formatPercent(analysis.transparentPercentage) : t('notAvailable')}</td>
          <td class="mono-value">${record.status === 'ready' ? formatPercent(analysis.semiTransparentPercentage) : t('notAvailable')}</td>
          <td><span class="padding-value">${padding}</span></td>
          <td>${risk}</td>
          <td>${renderRecordStatus(record, t)}</td>
          <td class="column-actions">
            <div class="row-actions">
              <button class="icon-button" type="button" data-action="reanalyze" data-record-id="${record.id}" aria-label="${t('reanalyzeFile', { name: escapeAttribute(record.file.name) })}" title="${t('reanalyzeFile', { name: escapeAttribute(record.file.name) })}" data-testid="row-reanalyze" ${record.status === 'analyzing' ? 'disabled' : ''}>↻</button>
              <button class="icon-button icon-button--danger" type="button" data-action="delete" data-record-id="${record.id}" aria-label="${t('deleteFile', { name: escapeAttribute(record.file.name) })}" title="${t('deleteFile', { name: escapeAttribute(record.file.name) })}" data-testid="row-delete">×</button>
            </div>
          </td>
        </tr>
      `;
    })
    .join('');
}

function renderAlphaStatus(
  record: UiRecord,
  realTransparency: boolean,
  t: ResultTableOptions['translate'],
): string {
  if (record.status !== 'ready') return `<span class="muted-value">${t('notAvailable')}</span>`;
  return realTransparency
    ? `<span class="status status--success"><span aria-hidden="true">✓</span>${t('yes')}</span>`
    : `<span class="status status--neutral"><span aria-hidden="true">×</span>${t('no')}</span>`;
}

function renderRecordStatus(record: UiRecord, t: ResultTableOptions['translate']): string {
  if (record.status === 'error') {
    return `<span class="status status--error" title="${escapeAttribute(record.error ?? '')}"><span aria-hidden="true">×</span><span data-testid="result-status">${t('statusError')}</span></span>`;
  }
  if (record.status === 'queued') {
    return `<span class="status status--neutral"><span aria-hidden="true">…</span><span data-testid="result-status">${t('statusQueued')}</span></span>`;
  }
  if (record.status === 'analyzing') {
    return `<span class="status status--working"><span class="working-glyph" aria-hidden="true">↻</span><span data-testid="result-status">${record.progress > 0 ? `${Math.round(record.progress)}%` : t('statusAnalyzing')}</span></span>`;
  }
  return `<span class="status status--success"><span aria-hidden="true">✓</span><span data-testid="result-status">${t('statusReady')}</span></span>`;
}

export function bindResultTable(container: HTMLElement, options: ResultTableOptions): void {
  container
    .querySelector<HTMLInputElement>('#result-search')
    ?.addEventListener('input', (event) => {
      options.onSearch((event.currentTarget as HTMLInputElement).value);
    });
  container
    .querySelector<HTMLSelectElement>('#result-filter')
    ?.addEventListener('change', (event) => {
      options.onFilter((event.currentTarget as HTMLSelectElement).value as ResultFilter);
    });
  container
    .querySelector<HTMLSelectElement>('#result-sort')
    ?.addEventListener('change', (event) => {
      options.onSort((event.currentTarget as HTMLSelectElement).value as ResultSort);
    });
  container
    .querySelector<HTMLInputElement>('[data-action="toggle-all"]')
    ?.addEventListener('change', (event) => {
      options.onToggleAll((event.currentTarget as HTMLInputElement).checked);
    });
  container
    .querySelectorAll<HTMLInputElement>('[data-action="toggle-record"]')
    .forEach((checkbox) => {
      checkbox.addEventListener('change', () =>
        options.onToggleRecord(checkbox.dataset.recordId ?? '', checkbox.checked),
      );
    });
  container.querySelectorAll<HTMLButtonElement>('[data-action="open"]').forEach((button) => {
    button.addEventListener('click', () => options.onOpen(button.dataset.recordId ?? ''));
  });
  container.querySelectorAll<HTMLButtonElement>('[data-action="delete"]').forEach((button) => {
    button.addEventListener('click', () => options.onDelete(button.dataset.recordId ?? ''));
  });
  container.querySelectorAll<HTMLButtonElement>('[data-action="reanalyze"]').forEach((button) => {
    button.addEventListener('click', () => options.onReanalyze(button.dataset.recordId ?? ''));
  });
  bindButton(container, 'delete-selected', options.onDeleteSelected);
  bindButton(container, 'reanalyze-selected', options.onReanalyzeSelected);
  bindButton(container, 'clear', options.onClear);
  bindButton(container, 'export-json', options.onExportJson);
  bindButton(container, 'export-csv', options.onExportCsv);
}

function bindButton(container: HTMLElement, action: string, callback: () => void): void {
  container
    .querySelector<HTMLButtonElement>(`[data-action="${action}"]`)
    ?.addEventListener('click', callback);
}

function option(value: string, label: string, selected: string): string {
  return `<option value="${value}" ${selected === value ? 'selected' : ''}>${label}</option>`;
}

function escapeHtml(value: string): string {
  const span = document.createElement('span');
  span.textContent = value;
  return span.innerHTML;
}

function escapeAttribute(value: string): string {
  return escapeHtml(value).replaceAll('`', '&#96;');
}
